import { z } from "zod";

/**
 * Shared contact-form validation. Imported by both the browser form and the
 * server function so the two can never drift apart.
 */

// Control characters are never legitimate in a form field and are a common
// header/log injection vector.
// eslint-disable-next-line no-control-regex -- matching control chars is the point here
const CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;

// Letters (any script), spaces, apostrophes, hyphens, dots and commas.
const NAME_ALLOWED = /^[\p{L}\p{M}][\p{L}\p{M}\s'’.,-]*$/u;

const URL_LIKE = /(https?:\/\/|www\.|\b[a-z0-9-]+\.(?:com|net|org|ru|xyz|info|top|click|shop)\b)/i;
const BBCODE = /\[url[=\]]/i;

const noControlChars = (value: string) => !CONTROL_CHARS.test(value);

export const CONTACT_LIMITS = {
  name: 100,
  email: 254,
  company: 150,
  message: 4000,
} as const;

export const MESSAGE_MIN = 20;

const collapse = (value: string) => value.replace(/\s+/g, " ").trim();

export const contactSchema = z.object({
  name: z
    .string()
    .transform(collapse)
    .pipe(
      z
        .string()
        .min(2, "Please enter your name (at least 2 characters)")
        .max(CONTACT_LIMITS.name, `Please keep your name under ${CONTACT_LIMITS.name} characters`)
        .refine(noControlChars, "Please remove any unusual characters from your name")
        .refine(
          (v) => NAME_ALLOWED.test(v),
          "Please use letters, spaces, hyphens and apostrophes only",
        )
        .refine((v) => !URL_LIKE.test(v), "Please enter a name, not a web address"),
    ),
  email: z
    .string()
    .transform((v) => v.trim())
    .pipe(
      z
        .string()
        .min(1, "Please enter your email address")
        .max(CONTACT_LIMITS.email, "That email address is too long")
        .email("Please enter a valid email address, for example jane@company.com")
        .refine(noControlChars, "Please remove any unusual characters from your email")
        .refine((v: string) => {
          const domain = v.split("@")[1] ?? "";
          // Require a real dotted domain with a 2+ character TLD.
          return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i.test(
            domain,
          );
        }, "Please check the part after the @. It does not look like a real domain")
        .refine(
          (v: string) => !/^(test|noreply|no-reply|donotreply)@/i.test(v),
          "Please use an address I can actually reply to",
        ),
    ),
  company: z
    .string()
    .transform(collapse)
    .pipe(
      z
        .string()
        .max(
          CONTACT_LIMITS.company,
          `Please keep the company name under ${CONTACT_LIMITS.company} characters`,
        )
        .refine(noControlChars, "Please remove any unusual characters from the company name"),
    )
    .optional()
    .default(""),
  message: z
    .string()
    .transform((v) =>
      v
        .replace(/\r\n/g, "\n")
        .replace(/[ \t]+/g, " ")
        .trim(),
    )
    .pipe(
      z
        .string()
        .min(MESSAGE_MIN, `Please add a little more detail (at least ${MESSAGE_MIN} characters)`)
        .max(
          CONTACT_LIMITS.message,
          `Please keep your message under ${CONTACT_LIMITS.message} characters`,
        )
        .refine((v) => collapse(v).split(" ").length >= 4, "Please write a few words so I can help")
        .refine(
          (v) => !BBCODE.test(v),
          "Your message looks like spam. Please email info@scryla.co.uk instead.",
        )
        .refine((v) => {
          const links = v.match(/https?:\/\//gi)?.length ?? 0;
          return links <= 2;
        }, "Please include no more than two links"),
    ),
  website: z.string().max(0).optional().default(""),
  turnstileToken: z.string().max(4096).optional().default(""),
});

export type ContactInput = z.input<typeof contactSchema>;
export type ContactField = "name" | "email" | "company" | "message";
export type ContactFieldErrors = Partial<Record<ContactField, string>>;

/** First error per field, in a shape both sides can render. */
export function toFieldErrors(error: z.ZodError): ContactFieldErrors {
  const out: ContactFieldErrors = {};
  for (const issue of error.issues) {
    const key = issue.path[0] as ContactField;
    if (key && !out[key]) out[key] = issue.message;
  }
  return out;
}

/** Validate a single field without surfacing errors for the others. */
export function validateField(field: ContactField, value: string): string | undefined {
  const shape = contactSchema.shape[field];
  const result = shape.safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}
